import { useQuery } from '@tanstack/react-query';

import { GeoCode } from '../types/GeoCode';
import { addressesAPI } from '../api/address';
import { captureApiError, captureError } from '../../../lib/sentry-utils';

/**
 * Nominatim ничего не нашёл по точке (море, пустырь, граница карты). Это не
 * сбой сети — повторять запрос бессмысленно, и в Sentry ему делать нечего.
 */
export class GeocodeNotFoundError extends Error {
  constructor(public readonly reason: string) {
    super(reason);
    this.name = 'GeocodeNotFoundError';
  }
}

export const isGeocodeNotFound = (error: unknown): error is GeocodeNotFoundError =>
  error instanceof GeocodeNotFoundError;

export const useUserAddress = ({
  lat,
  lng,
  enabled = true,
}: GeoCode & { enabled?: boolean }) => {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['user-address', lat, lng],

    queryFn: async () => {
      try {
        const res = await addressesAPI.getUserAddress({ lat, lng });

        if (res?.data?.error) {
          throw new GeocodeNotFoundError(res.data.error);
        }

        const formattedAddress = res?.data?.display_name;

        if (!formattedAddress) {
          captureError('Invalid geocoding response structure', {
            endpoint: '/nominatim/reverse',
            lat,
            lng,
            hasResponse: Boolean(res?.data),
          });
          throw new Error('Invalid geocoding response structure');
        }

        return formattedAddress;
      } catch (error) {
        if (isGeocodeNotFound(error)) throw error;

        console.error(
          'GetUserLocation error:',
          JSON.stringify(error, Object.getOwnPropertyNames(error), 2)
        );
        captureApiError(error, '/nominatim/reverse', 'GET');

        throw error;
      }
    },
    enabled: enabled && !!lat && !!lng,
    retry: (failureCount, error) =>
      !isGeocodeNotFound(error) && failureCount < 3,
  });

  return { address: data, isLoading, isError, error, refetch };
};
